import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import GetGeekLogo from "../../src/assets/images/GetGeekLogo.png";

import {
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  ContactMail as ContactMailIcon,
  SupportAgent as SupportAgentIcon,
  Feedback as FeedbackIcon,
  Assessment as AssessmentIcon,
  LocalOffer as LocalOfferIcon,
  TrendingUp as TrendingUpIcon,
  CardGiftcard as CardGiftcardIcon,
  MenuBook as MenuBookIcon,
  Menu as MenuIcon,
  ChevronLeft,
  ChevronRight,
  MonitorHeart as MonitorHeartIcon,
  Share as ShareIcon,
} from "@mui/icons-material";

const Sidebar = ({ onSidebarStateChange }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const menuItems = [
    { text: "Dashboard", icon: <DashboardIcon />, path: "/Dashboard" },
    {
      text: "Server Status",
      icon: <MonitorHeartIcon />,
      path: "/ServerStatus",
    },
    {
      text: "Getgeek Leads",
      icon: <SupportAgentIcon />,
      path: "/GetgeekLeads",
    },
    {
      text: "Companies",
      icon: <BusinessIcon />,
      path: "/CompaniesDashboard",
      relatedPaths: [
        "/TwoTabsPage",
        "/resume-screening-candidates",
        "/interview-candidates",
      ],
    },
    {
      text: "Recruiters",
      icon: <PeopleIcon />,
      path: "/Recruiters",
    },
    {
      text: "Contact Leads",
      icon: <ContactMailIcon />,
      path: "/ContactData",
    },
    {
      text: "Offer Leads",
      icon: <CardGiftcardIcon />,
      path: "/OfferLeads",
    },
    {
      text: "ROI Leads",
      icon: <TrendingUpIcon />,
      path: "/RoiLeads",
    },
    {
      text: "Feedback For Getgeek",
      icon: <FeedbackIcon />,
      path: "/GetgeekFeedback",
    },
    {
      text: "Feedbacks For Interview",
      icon: <AssessmentIcon />,
      path: "/Result",
    },

    {
      text: "Manage Offers",
      icon: <LocalOfferIcon />,
      path: "/OffersManagement",
    },

    {
      text: "User Guides",
      icon: <MenuBookIcon />,
      path: "/UserGuides",
    },
    {
      text: "Customer Support",
      icon: <SupportAgentIcon />,
      path: "/CustomerSupport",
    },
    {
      text: "News Letter",
      icon: <SupportAgentIcon />,
      path: "/NewsLetter",
    },
    {
      text: "Connect Socials",
      icon: <ShareIcon />,
      path: "/ConnectSocials",
    },
  ];

  // Notify parent component about sidebar state changes
  useEffect(() => {
    if (onSidebarStateChange) {
      onSidebarStateChange(desktopOpen);
    }
  }, [desktopOpen, onSidebarStateChange]);

  // Set active index based on current route
  useEffect(() => {
    const currentPath = location.pathname;

    const currentIndex = menuItems.findIndex((item) => {
      if (item.path === currentPath) {
        return true;
      }
      if (item.relatedPaths) {
        return item.relatedPaths.some((relatedPath) => {
          if (relatedPath === currentPath) {
            return true;
          }
          if (currentPath.startsWith(relatedPath)) {
            return true;
          }
          return false;
        });
      }
      return false;
    });

    if (currentIndex !== -1) {
      setActiveIndex(currentIndex);
    }
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopToggle = () => {
    setDesktopOpen(!desktopOpen);
  };

  const handleMenuItemClick = (index, path) => {
    setActiveIndex(index);
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawerContent = (isDesktop = false) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: 3, pt: isMobile ? 2 : 3, flexShrink: 0 }}>
        {!isMobile && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <img
              src={GetGeekLogo}
              alt="GetGeek Logo"
              style={{
                width: desktopOpen ? "150px" : "80px",
                objectFit: "contain",
                transition: "width 0.3s ease",
              }}
            />
          </Box>
        )}
        {isMobile && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              mb: 2,
            }}
          >
            <img
              src={GetGeekLogo}
              alt="GetGeek Logo"
              style={{
                width: "60px",
                height: "60px",
                marginBottom: "12px",
                objectFit: "contain",
              }}
            />
          </Box>
        )}
      </Box>
      <Divider sx={{ mt: isMobile ? 1 : 0, flexShrink: 0 }} />

      {/* Scrollable nav — keeps logo + collapse control fixed */}
      <List
        sx={{
          px: 1,
          pt: 2,
          pb: 2,
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          // Thin scrollbar so last items (e.g. Connect Socials) stay reachable
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#CBD5E0",
            borderRadius: 3,
          },
        }}
      >
        {menuItems.map((item, index) => {
          const isActive = index === activeIndex;
          return (
            <ListItem key={index} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => handleMenuItemClick(index, item.path)}
                sx={{
                  borderRadius: 2,
                  backgroundColor: "transparent",
                  color: isActive ? "#2B3674" : "#A3AED0",
                  "&:hover": {
                    backgroundColor: "#F7FAFC",
                    color: "#2B3674",
                  },
                  py: isMobile ? 2 : 1.5,
                  px: 1,
                  justifyContent:
                    !desktopOpen && !isMobile ? "center" : "flex-start",
                  minHeight: 48,
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive ? "#9048FB" : "#A3AED0",
                    minWidth: 32,
                    justifyContent: "center",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {(desktopOpen || isMobile) && (
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: isMobile ? "1.1rem" : "1rem",
                      fontFamily: "Outfit",
                      fontWeight: isActive ? 700 : 500,
                      color: isActive ? "#2B3674" : "#A3AED0",
                      whiteSpace: "nowrap",
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Desktop toggle button at bottom */}
      {isDesktop && (
        <Box
          sx={{
            p: 2,
            borderTop: "1px solid #E2E8F0",
            display: "flex",
            justifyContent: desktopOpen ? "flex-end" : "center",
            flexShrink: 0,
            backgroundColor: "#FFFFFF",
          }}
        >
          <IconButton
            onClick={handleDesktopToggle}
            sx={{
              color: "#A3AED0",
              backgroundColor: desktopOpen ? "#F7FAFC" : "transparent",
              "&:hover": {
                backgroundColor: desktopOpen ? "#E2E8F0" : "#F7FAFC",
                color: "#2B3674",
              },
              size: "small",
              border: desktopOpen ? "1px solid #E2E8F0" : "none",
              boxShadow: desktopOpen ? "0 2px 4px rgba(0,0,0,0.1)" : "none",
            }}
          >
            {desktopOpen ? <ChevronLeft /> : <ChevronRight />}
          </IconButton>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      {/* Mobile Header with Menu Button */}
      {isMobile && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: 64,
            backgroundColor: "#FFFFFF",
            borderBottom: "1px solid #E2E8F0",
            display: "flex",
            alignItems: "center",
            px: 2,
            zIndex: 1200,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              mr: 2,
              color: "#2B3674",
              "&:hover": {
                backgroundColor: "#F7FAFC",
              },
            }}
          >
            <MenuIcon />
          </IconButton>
        </Box>
      )}

      {/* Permanent Drawer for lg+ screens */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: desktopOpen ? 240 : 100,
            flexShrink: 0,
            transition: "width 0.3s ease",
            "& .MuiDrawer-paper": {
              width: desktopOpen ? 220 : 100,
              height: "100%",
              maxHeight: "100dvh",
              boxSizing: "border-box",
              backgroundColor: "#FFFFFF",
              border: "none",
              transition: "width 0.3s ease",
              overflow: "hidden",
              boxShadow: desktopOpen ? "none" : "2px 0 8px rgba(0,0,0,0.1)",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          {drawerContent(true)}
        </Drawer>
      )}

      {/* Temporary Drawer for mobile screens */}
      {isMobile && mobileOpen && (
        <>
          <Box
            onClick={handleDrawerToggle}
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.3)",
              zIndex: 1200,
            }}
          />

          <Box
            sx={{
              position: "fixed",
              top: 80,
              left: 16,
              width: 280,
              maxHeight: "calc(100vh - 120px)",
              backgroundColor: "#FFFFFF",
              borderRadius: 3,
              boxShadow:
                "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)",
              zIndex: 1300,
              transform: "translateX(0)",
              transition: "transform 0.3s ease-in-out",
              overflow: "hidden",
              border: "1px solid #E2E8F0",
            }}
          >
            <Box sx={{ overflowY: "auto", height: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 2,
                  borderBottom: "1px solid #E2E8F0",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  <img
                    src={GetGeekLogo}
                    alt="GetGeek Logo"
                    style={{
                      width: "50px",
                      height: "50px",
                      marginBottom: "8px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <IconButton
                  onClick={handleDrawerToggle}
                  sx={{
                    color: "#A3AED0",
                    "&:hover": {
                      backgroundColor: "#F7FAFC",
                      color: "#2B3674",
                    },
                  }}
                >
                  ✕
                </IconButton>
              </Box>

              <List sx={{ px: 1, py: 2 }}>
                {menuItems.map((item, index) => {
                  const isActive = index === activeIndex;
                  return (
                    <ListItem key={index} disablePadding sx={{ mb: 1 }}>
                      <ListItemButton
                        onClick={() => handleMenuItemClick(index, item.path)}
                        sx={{
                          borderRadius: 2,
                          backgroundColor: "transparent",
                          color: isActive ? "#2B3674" : "#A3AED0",
                          "&:hover": {
                            backgroundColor: "#F7FAFC",
                            color: "#2B3674",
                          },
                          py: 2,
                          px: 1,
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            color: isActive ? "#9048FB" : "#A3AED0",
                            minWidth: 32,
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            fontSize: "1.1rem",
                            fontFamily: "Outfit",
                            fontWeight: isActive ? 700 : 500,
                            color: isActive ? "#2B3674" : "#A3AED0",
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          </Box>
        </>
      )}

      {/* Mobile content spacer */}
      {isMobile && <Box sx={{ height: 64, width: "100%" }} />}
    </Box>
  );
};

export default Sidebar;
